// frontend/src/components/mcbim/BuildingStoryMenu.tsx
import React, { useMemo } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { MdApartment, MdLayers } from "react-icons/md";
import { FiCheck, FiChevronDown } from "react-icons/fi";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectBuildingStoriesGroupedByBuilding, selectBuildingStoryList } from "../designSelector";
import {
  setBuildingStoryListRedux,
  setFloorAndJobInRedux,
  setSelectedBuildingInRedux,
} from "../designSlice";

export const BuildingStoryMenu: React.FC = () => {
  const dispatch = useAppDispatch();

  const floorAndJob = useAppSelector((state) => state.design.floorAndJob);
  const selectedBuilding = useAppSelector((state) => state.design.selectedBuilding);
  const buildingStoriesState = useAppSelector((state) => state.design.buildingStoryList);

  const filteredStories = useAppSelector(selectBuildingStoryList);
  const grouped = useAppSelector(selectBuildingStoriesGroupedByBuilding);

  const tooltipLabel = floorAndJob ? "Endre etasje" : "Velg etasje";

  const setSearch = (search: string) => {
    dispatch(setBuildingStoryListRedux({ ...buildingStoriesState, search }));
  };

  const activeBuilding = useMemo(() => {
    if (selectedBuilding && grouped.some((g) => g.building === selectedBuilding)) {
      return selectedBuilding;
    }
    return grouped[0]?.building ?? null;
  }, [grouped, selectedBuilding]);

  const activeGroup = useMemo(() => {
    return grouped.find((g) => g.building === activeBuilding) ?? null;
  }, [grouped, activeBuilding]);

  const handleSelectBuilding = (building: string) => {
    dispatch(setSelectedBuildingInRedux(building));
  };

  const handleSelectFloor = (story: any) => {
    const floorName = story.name ?? story.buildingStoryName;
    const floorGlobalId = story.globalId ?? story.buildingStoryGlobalId;
    const jobId = story.job?.jobId ?? story.jobId;
    const elevation = story.elevation ?? story.buildingStoryElevation ?? undefined;

    dispatch(
      setFloorAndJobInRedux({
        floorName,
        jobId,
        floorGlobalId,
        elevation,
      })
    );

    if (activeBuilding) {
      dispatch(setSelectedBuildingInRedux(activeBuilding));
    }
  };

  return (
    <Menu
      withinPortal
      position="bottom-start"
      shadow="xl"
      width={680}
      closeOnItemClick={false}
      styles={{
        dropdown: {
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(8px)",
        },
      }}
    >
      <Menu.Target>
        <Tooltip label={tooltipLabel} withArrow position="bottom">
          <Button
            size="sm"
            variant="subtle"
            leftSection={<MdLayers size={16} />}
            rightSection={<FiChevronDown size={14} />}
            w={280}
            justify="space-between"
            styles={{ label: { width: "100%" } }}
          >
            <Text truncate fw={500}>
              {floorAndJob?.floorName ?? "Velg etasje"}
            </Text>
          </Button>
        </Tooltip>
      </Menu.Target>

      <Menu.Dropdown>
        {/* Search */}
        <Box p="md" pb="xs">
          <TextInput
            value={buildingStoriesState.search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder="Søk etter etasje eller bygning…"
            size="xs"
          />
        </Box>

        <Divider />

        {filteredStories.length === 0 ? (
          <Box p="xl" ta="center">
            <Text size="sm" c="dimmed">
              Ingen etasjer matcher søket.
            </Text>
          </Box>
        ) : (
          <>
            {/* Column headers */}
            <Box style={{ display: "flex" }} p="md" pb={8}>
              <Box style={{ flex: "0 0 260px", paddingLeft: 12 }}>
                <Text fw={700} size="sm">
                  Bygninger
                </Text>
              </Box>
              <Box style={{ flex: 1, paddingLeft: 12 }}>
                <Text fw={700} size="sm">
                  {activeBuilding ? `Etasjer i ${activeBuilding}` : "Etasjer"}
                </Text>
              </Box>
            </Box>

            <Divider />

            {/* Two-column layout */}
            <Box style={{ display: "flex", height: 420 }}>
              {/* Left: Buildings */}
              <Box
                style={{
                  flex: "0 0 260px",
                  borderRight: "1px solid var(--mantine-color-gray-3)",
                }}
              >
                <ScrollArea h="100%" type="hover">
                  <Stack gap={0}>
                    {grouped.map((g) => {
                      const isActive = g.building === activeBuilding;

                      return (
                        <NavLink
                          key={g.building}
                          active={isActive}
                          label={
                            <Group justify="space-between" wrap="nowrap" gap={8} w="100%">
                              <Text truncate fw={isActive ? 600 : 400}>
                                {g.building}
                              </Text>
                              <Badge variant="light" color="blue" size="xs">
                                {g.floors.length}
                              </Badge>
                            </Group>
                          }
                          leftSection={<MdApartment size={16} />}
                          onClick={() => handleSelectBuilding(g.building)}
                          variant="subtle"
                          styles={{
                            root: {
                              borderRadius: 0,
                              padding: "10px 12px",
                            },
                            label: { width: "100%" },
                          }}
                        />
                      );
                    })}
                  </Stack>
                </ScrollArea>
              </Box>

              {/* Right: Floors */}
              <Box style={{ flex: 1 }}>
                <ScrollArea h="100%" type="hover">
                  <Stack gap={0} p="md">
                    {!activeGroup ? (
                      <Text size="sm" c="dimmed" p="md" ta="center">
                        Velg en bygning til venstre
                      </Text>
                    ) : activeGroup.floors.length === 0 ? (
                      <Text size="sm" c="dimmed" p="md" ta="center">
                        Ingen etasjer i denne bygningen
                      </Text>
                    ) : (
                      activeGroup.floors.map((story: any) => {
                        const floorName = story.name ?? story.buildingStoryName;
                        const jobId = story.job?.jobId ?? story.jobId;
                        const isSelected =
                          floorAndJob?.floorName === floorName && floorAndJob?.jobId === jobId;
                        const spaceCount = story.spaceCount;

                        return (
                          <Menu.Item
                            key={[
                              activeGroup.building,
                              story.globalId ?? story.buildingStoryGlobalId,
                              jobId,
                            ].join("_")}
                            onClick={() => handleSelectFloor(story)}
                            leftSection={
                              isSelected ? (
                                <FiCheck size={16} color="var(--mantine-color-blue-6)" />
                              ) : null
                            }
                            rightSection={
                              typeof spaceCount === "number" && spaceCount > 0 ? (
                                <Badge variant="light" color="gray" size="xs">
                                  {spaceCount} ROM
                                </Badge>
                              ) : null
                            }
                            styles={{
                              item: {
                                padding: "10px 12px",
                                borderRadius: 4,
                              },
                            }}
                          >
                            <Text size="sm" truncate>
                              {floorName}
                            </Text>
                          </Menu.Item>
                        );
                      })
                    )}
                  </Stack>
                </ScrollArea>
              </Box>
            </Box>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};
